import { Component, EventEmitter, Output, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { openClose } from '@core/animations/animations';
import { getMimeType } from '@core/util/mime';
import {
	getCounselFeeHover,
	getPremiumPillHover,
	TitleInsurancePillData,
} from '@core/util/title-insurance';
import { FCTFunctions } from '@functions/fct.service';
import { MatterBusinessUser } from '@models/matter';
import { MatterFile } from '@models/matter-file';
import { MatterFCTResidentialTitleInsurance } from '@models/title-insurance/fct';
import {
	FCTDocumentType,
	FCTTitleInsuranceState,
	TitleInsuranceDocument,
	TitleInsurancePolicy,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import LogRocket from 'logrocket';
import { combineLatest, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import {
	CostBreakdownComponent,
	FCTTitleInsuranceCostBreakdownData,
} from '../cost-breakdown/cost-breakdown.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import { TestHooks } from './completed.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

export interface FCTDocuments extends TitleInsuranceDocument {
	loading: boolean;
}

@UntilDestroy()
@Component({
	selector: 'app-fct-completed',
	templateUrl: './completed.component.html',
	styleUrls: ['./completed.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class FCTCompletedComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public cancellationPackageUrl: string;
	public confirmationPackageUrl: string;
	public states = FCTTitleInsuranceState;
	public confirmDocName = FCTDocumentType.Confirmation_Package;
	public cancelDocName = FCTDocumentType.Cancellation_Package;
	public confirmationFile: MatterFile;
	public cancellationFile: MatterFile;
	public downloadingConfirmationFile = false;
	public downloadingCancellationFile = false;
	public gettingStatus = false;
	public loading = false;
	public displayPill = 'none';
	public premiumPillData: TitleInsurancePillData;
	public counselPillData: TitleInsurancePillData;
	public documents: FCTDocuments[] = [];
	public titleInsurance: MatterFCTResidentialTitleInsurance;

	@Output() openModal: EventEmitter<void> = new EventEmitter();

	@SelectSnapshot(SelectedMatter.assignedLawyer) lawyer: MatterBusinessUser;
	@ViewSelectSnapshot(SelectedMatter.titleInsuranceThirdPartyId)
	thirdPartyId: string;
	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterFCTResidentialTitleInsurance>;
	@Select(SelectedMatter.titleInsurancePolicies) policies$: Observable<
		TitleInsurancePolicy[]
	>;
	@Select(SelectedMatter.titleInsuranceAmountDueByClient)
	amountDueByClient$: Observable<number>;
	@Select(SelectedMatter.getTitleInsuranceDirectionOfAmount)
	titleInsuranceDOA$: Observable<DirectionOfFeeType>;

	drawerService = inject(DrawerService);

	constructor(
		private dialog: MatDialog,
		private fctFunctions: FCTFunctions,
		private analytics: AnalyticsService,
	) {}

	async selectedDoc(document: FCTDocuments) {
		this.loading = true;

		try {
			document.loading = true;
			LogRocket.log('Fetching FCT Document', document.name);

			const doc = await this.fctFunctions
				.getDocument(
					this.thirdPartyId,
					this.lawyer.id,
					document.name as FCTDocumentType,
				)
				.pipe(take(1))
				.toPromise()
				.finally(() => (document.loading = false));

			LogRocket.log('FCT document successfully downloaded');

			const data: PdfModalData = {
				base64Encoded: {
					data: doc.data,
					encoding: getMimeType[doc.fileFormat.toLowerCase()],
				},
				fileName: document.name,
			};

			this.openPDF(data);
		} catch (err) {
			console.log('Unable to download and open FCT document', err);
			LogRocket.error('Unable to download and open FCT document', err);
		} finally {
			this.loading = false;
		}
	}

	ngOnInit() {
		combineLatest([this.titleInsurance$, this.titleInsuranceDOA$])
			.pipe(untilDestroyed(this))
			.subscribe(([_titleInsurance, titleInsuranceDOA]) => {
				if (_titleInsurance) {
					this.titleInsurance = _titleInsurance;
				}

				this.premiumPillData = getPremiumPillHover(titleInsuranceDOA);
				this.counselPillData = getCounselFeeHover(
					_titleInsurance,
					titleInsuranceDOA,
				);

				this.documents = _titleInsurance?.documents?.map((doc) => ({
					...doc,
					loading: false,
				}));
			});
	}

	openPortal() {
		this.openModal.emit();
	}

	showBreakdown() {
		this.analytics.logEvent(AnalyticsEvent.TITLE_INSURANCE_VIEW_FEE_BREAKDOWN, {
			provider: TitleInsuranceProvider.FCT,
		});
		const data: FCTTitleInsuranceCostBreakdownData = {
			type: 'ORDER',
		};
		this.dialog.open(CostBreakdownComponent, { data });
	}

	togglePill(_displayPill: string) {
		this.displayPill = _displayPill;
	}

	policyTypeString(type: string): string {
		if (type === 'lender') {
			return 'Lender policy';
		}

		if (type === 'owner') {
			return 'Owner policy';
		}

		return type;
	}

	openPDF(data: PdfModalData) {
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: data.fileName,
			noStyle: true,
		});
	}
}
